/** 分公司（销售办公室）选项 */
export interface SalesOfficeItem {
  id: string;
  code: string;
  name: string;
}

export interface SalesOfficeQueryParams {
  currentPage: number;
  size: number;
  /** 按 code / name 模糊搜索关键词 */
  keyword?: string;
}

export interface SalesOfficePageResult {
  content: SalesOfficeItem[];
  totalPages: number;
  totalElements: number;
}

interface SalesOfficeQueryResponse {
  data?: {
    ariesSalesOfficeQuery?: {
      queryPage?: {
        content?: SalesOfficeItem[];
        totalPages?: number | string;
        totalElements?: number | string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

/** 接口原始行 */
export interface ReceivableCustomerRawItem {
  id: string;
  /** 应收报表勾选导出用（列表不展示） */
  masterId?: string;
  feedbackStatus?: string;
  feedbackMessage?: string;
  feedbackUserName?: string;
  feedbackDate?: string;
  salesOfficeRegionName?: string;
  /** 公司编码 */
  corpCode?: string;
  saleOfficeName?: string;
  dissaleChannelCode?: string;
  dissaleChannelName?: string;
  saleChannelName?: string;
  controlUserName?: string;
  controlUserCode?: string;
  sapCustomerCode?: string;
  custCode?: string;
  custName?: string;
  paymentConditionName?: string;
  remainingReceivableAmt?: number | string;
  xyqxzCny?: number | string;
  yqTotalCny?: number | string;
  yqMonth01Cny?: number | string;
  yqMonth02Cny?: number | string;
  yqMonth03Cny?: number | string;
  yqMonth04Cny?: number | string;
  yqMonth05Cny?: number | string;
  yqMonth06Cny?: number | string;
  yqMonth07Cny?: number | string;
  yqMonth08Cny?: number | string;
  yqMonth09Cny?: number | string;
  yqMonth10Cny?: number | string;
  yqMonth11Cny?: number | string;
  yqMonth12Cny?: number | string;
  yqMonth13Cny?: number | string;
  riskLevel?: string;
  riskReason?: string;
  riskDesc?: string;
  /** 是否人工修改风险：1=是 */
  riskManualModified?: number | string | boolean;
  /** 是否专业客户：1=是，0=否 */
  isProfessional?: number | string | boolean;
  /** 是否起诉客户：1=是，0=否 */
  isProsecute?: number | string | boolean;
  financeName?: string;
  financeCode?: string;
  salesmanUserName?: string;
  salesmanUserCode?: string;
  managerName?: string;
  managerCode?: string;
  dwsUpdateTime?: string;
  /** 数据同步时间（列表展示用） */
  lDate?: string;
  /** 原始到期应收 JSON */
  dqysJson?: string;
  /** 后端冲抵后的到期应收 JSON */
  dqysDisplayJson?: string;
}

/** 列表展示行 */
export interface ReceivableCustomerItem {
  id: string;
  /** 应收报表勾选导出用（列表不展示） */
  masterId?: string;
  /** 合计行（统计接口），不可勾选、操作列显示「合计」 */
  isSummaryRow?: boolean;
  status?: string;
  latestFeedback?: string;
  latestFeedbackBy?: string;
  latestFeedbackAt?: string;
  financeArea?: string;
  /** 公司 */
  corpCode?: string;
  companyName?: string;
  /** 销售渠道 */
  saleChannelName?: string;
  channel?: string;
  /** 渠道原始编码 10/20，反馈弹窗隐藏字段用 */
  dissaleChannelCode?: string;
  /** 同一控制人：列表展示 controlUserCode */
  controller?: string;
  customerCode?: string;
  customerName?: string;
  paymentTerm?: string;
  receivableBalance?: number | string;
  creditWithin?: number | string;
  overdueTotal?: number | string;
  overdue1?: number | string;
  overdue2?: number | string;
  overdue3?: number | string;
  overdue4?: number | string;
  overdue5?: number | string;
  overdue6?: number | string;
  overdue7?: number | string;
  overdue8?: number | string;
  overdue9?: number | string;
  overdue10?: number | string;
  overdue11?: number | string;
  overdue12?: number | string;
  overdueOverYear?: number | string;
  riskLevel?: string;
  riskReason?: string;
  overdueReason?: string;
  /** 是否人工修改风险：true 时列表展示「人工改」标签 */
  riskManualModified?: boolean;
  /** 是否专业客户：是/否 */
  isProfessional?: string;
  /** 是否起诉客户：是/否 */
  isProsecute?: string;
  financeOwner?: string;
  financeOwnerNo?: string;
  salesman?: string;
  salesmanNo?: string;
  companyManager?: string;
  companyManagerNo?: string;
  syncAt?: string;
  /** 反馈弹窗展示用 JSON（优先后端冲抵后的 dqysDisplayJson） */
  dqysJson?: string;
}

export interface ReceivableCustomerQueryParams {
  currentPage: number;
  size: number;
  /** 财务大区 EAST/WEST/SOUTH/NORTH */
  salesOfficeRegionCode?: string;
  /** 分公司编码（与后端 queryData.saleOfficeCode 对齐） */
  saleOfficeCode?: string;
  /** 财务负责人（工号或名称，后端兼容） */
  financeCode?: string;
  /** 渠道 10/20 */
  dissaleChannelCode?: string;
  /** 客户（编码或名称，后端兼容） */
  sapCustomerCode?: string;
  /** 业务员（工号或名称，后端兼容） */
  salesmanUserCode?: string;
  feedbackStatus?: string;
  riskLevel?: string;
  /** 同一控制人（编码或名称，后端兼容） */
  controlUserCode?: string;
  /** 是否有同一控制人；未选不传 */
  hasControlUser?: boolean;
  /** 是否起诉客户；未选不传 */
  isProsecute?: boolean;
  /** 是否逾期；未选不传 */
  isOverdue?: boolean;
}

interface ReceivableCustomerQueryResponse {
  data?: {
    ariesPlatformReceivableCustomerQuery?: {
      queryPage?: {
        content?: ReceivableCustomerRawItem[];
        totalElements?: number | string;
        totalPages?: number | string;
        size?: number | string;
        currentPage?: number | string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

function encodeBase64(value: string) {
  return window.btoa(unescape(encodeURIComponent(value)));
}

function escapeRsqlLikeValue(value: string) {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

function buildSalesOfficeGraphql(currentPage: number, size: number, rsql: string) {
  return `{
  ariesSalesOfficeQuery {
    queryPage(
      page: {currentPage: ${currentPage}, size: ${size}}
      queryWrapper: {rsql: ${JSON.stringify(rsql)}, queryData: "{}"}
    ) {
      content {
        code
        name
        id
      }
      totalPages
      totalElements
    }
  }
}`;
}

function buildSalesOfficeRsql(keyword?: string) {
  const kw = (keyword ?? '').trim();
  if (!kw) {
    return '';
  }
  const escaped = escapeRsqlLikeValue(kw);
  return `base64:${encodeBase64(`(code=like='${escaped}') or (name=like='${escaped}')`)}`;
}

/**
 * 分页查询分公司（销售办公室）
 * 支持下拉滚动加载下一页
 */
export async function querySalesOfficePage(
  params: SalesOfficeQueryParams,
): Promise<SalesOfficePageResult> {
  const rsql = buildSalesOfficeRsql(params.keyword);
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      variables: {
        path: '/aries_core_platform/uiMenu404d2c0193034412aaa55cfeda7e7537',
      },
      query: buildSalesOfficeGraphql(params.currentPage, params.size, rsql),
    }),
  });

  if (!response.ok) {
    throw new Error(`分公司接口请求失败: ${response.status}`);
  }

  const result: SalesOfficeQueryResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '分公司 GraphQL 请求失败');
  }

  const pageData = result.data?.ariesSalesOfficeQuery?.queryPage;
  return {
    content: uniqueSalesOffices(pageData?.content ?? []),
    totalPages: Number(pageData?.totalPages ?? 0),
    totalElements: Number(pageData?.totalElements ?? 0),
  };
}

function uniqueSalesOffices(items: SalesOfficeItem[]) {
  const map = new Map<string, SalesOfficeItem>();
  items.forEach((item) => {
    const code = (item?.code ?? '').trim();
    if (!code || map.has(code)) return;
    map.set(code, { ...item, code, name: (item?.name ?? '').trim() });
  });
  return [...map.values()];
}

function emptyToBlank(value?: string) {
  return (value ?? '').trim();
}

function buildReceivableQueryData(params: ReceivableCustomerQueryParams) {
  const data: Record<string, string | boolean> = {
    salesOfficeRegionCode: emptyToBlank(params.salesOfficeRegionCode),
    saleOfficeCode: emptyToBlank(params.saleOfficeCode),
    sapCustomerCode: emptyToBlank(params.sapCustomerCode),
    financeCode: emptyToBlank(params.financeCode),
    salesmanUserCode: emptyToBlank(params.salesmanUserCode),
    dissaleChannelCode: emptyToBlank(params.dissaleChannelCode),
    feedbackStatus: emptyToBlank(params.feedbackStatus),
    riskLevel: emptyToBlank(params.riskLevel),
    controlUserCode: emptyToBlank(params.controlUserCode),
  };
  if (typeof params.hasControlUser === 'boolean') {
    data.hasControlUser = params.hasControlUser;
  }
  if (typeof params.isProsecute === 'boolean') {
    data.isProsecute = params.isProsecute;
  }
  if (typeof params.isOverdue === 'boolean') {
    data.isOverdue = params.isOverdue;
  }
  return data;
}

function buildReceivableCustomerGraphql(
  currentPage: number,
  size: number,
  queryData: string,
) {
  return `{
  ariesPlatformReceivableCustomerQuery {
    queryPage(
      page: {currentPage: ${currentPage}, size: ${size}}
      queryWrapper: {queryData: ${JSON.stringify(queryData)}}
    ) {
      content {
        id
        masterId
        dissaleChannelCode
        sapCustomerCode
        riskLevel
        riskReason
        riskDesc
        riskManualModified
        isProfessional
        isProsecute
        feedbackStatus
        feedbackMessage
        feedbackUserName
        feedbackDate
        custCode
        custName
        saleOfficeCode
        saleOfficeName
        corpCode
        saleChannelName
        dissaleChannelName
        controlUserName
        controlUserCode
        salesmanUserCode
        salesmanUserName
        paymentConditionName
        remainingReceivableAmt
        xyqxzCny
        yqTotalCny
        yqMonth01Cny
        yqMonth02Cny
        yqMonth03Cny
        yqMonth04Cny
        yqMonth05Cny
        yqMonth06Cny
        yqMonth07Cny
        yqMonth08Cny
        yqMonth09Cny
        yqMonth10Cny
        yqMonth11Cny
        yqMonth12Cny
        yqMonth13Cny
        dwsUpdateTime
        lDate
        salesOfficeRegionCode
        salesOfficeRegionName
        financeCode
        financeName
        managerCode
        managerName
        dqysJson
        dqysDisplayJson
      }
      totalElements
      totalPages
      size
      currentPage
    }
  }
}`;
}

function formatChannel(code?: string, name?: string) {
  if (name) return name;
  if (code === '10') return '10渠道';
  if (code === '20') return '20渠道';
  return code || '';
}

function isRiskManualModified(value?: number | string | boolean) {
  return value === 1 || value === '1' || value === true;
}

function formatYesNo(value?: number | string | boolean) {
  if (value === 1 || value === '1' || value === true) return '是';
  if (value === 0 || value === '0' || value === false) return '否';
  return '-';
}

function mapReceivableCustomerItem(raw: ReceivableCustomerRawItem): ReceivableCustomerItem {
  return {
    id: String(raw.id),
    masterId: raw.masterId != null && raw.masterId !== '' ? String(raw.masterId) : undefined,
    status: raw.feedbackStatus,
    latestFeedback: raw.feedbackMessage,
    latestFeedbackBy: raw.feedbackUserName,
    latestFeedbackAt: raw.feedbackDate,
    isProfessional: formatYesNo(raw.isProfessional),
    financeArea: raw.salesOfficeRegionName,
    corpCode: raw.corpCode,
    companyName: raw.saleOfficeName,
    saleChannelName: raw.saleChannelName,
    channel: formatChannel(raw.dissaleChannelCode, raw.dissaleChannelName),
    dissaleChannelCode: raw.dissaleChannelCode,
    controller: raw.controlUserCode,
    customerCode: raw.sapCustomerCode || raw.custCode,
    customerName: raw.custName,
    paymentTerm: raw.paymentConditionName,
    receivableBalance: raw.remainingReceivableAmt,
    creditWithin: raw.xyqxzCny,
    overdueTotal: raw.yqTotalCny,
    overdue1: raw.yqMonth01Cny,
    overdue2: raw.yqMonth02Cny,
    overdue3: raw.yqMonth03Cny,
    overdue4: raw.yqMonth04Cny,
    overdue5: raw.yqMonth05Cny,
    overdue6: raw.yqMonth06Cny,
    overdue7: raw.yqMonth07Cny,
    overdue8: raw.yqMonth08Cny,
    overdue9: raw.yqMonth09Cny,
    overdue10: raw.yqMonth10Cny,
    overdue11: raw.yqMonth11Cny,
    overdue12: raw.yqMonth12Cny,
    overdueOverYear: raw.yqMonth13Cny,
    riskLevel: raw.riskLevel,
    riskReason: raw.riskReason,
    overdueReason: raw.riskDesc,
    isProsecute: formatYesNo(raw.isProsecute),
    riskManualModified: isRiskManualModified(raw.riskManualModified),
    financeOwner: raw.financeName,
    financeOwnerNo: raw.financeCode,
    salesman: raw.salesmanUserName,
    salesmanNo: raw.salesmanUserCode,
    companyManager: raw.managerName,
    companyManagerNo: raw.managerCode,
    syncAt: raw.lDate,
    // 弹窗展示用后端已冲抵结果，避免前端再次冲抵
    dqysJson: raw.dqysDisplayJson || raw.dqysJson,
  };
}

/**
 * 应收客户分页查询
 */
export async function queryReceivableCustomerPage(
  params: ReceivableCustomerQueryParams,
): Promise<{ content: ReceivableCustomerItem[]; totalElements: number }> {
  const queryData = JSON.stringify(buildReceivableQueryData(params));
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildReceivableCustomerGraphql(params.currentPage, params.size, queryData),
    }),
  });

  if (!response.ok) {
    throw new Error(`应收客户接口请求失败: ${response.status}`);
  }

  const result: ReceivableCustomerQueryResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '应收客户 GraphQL 请求失败');
  }

  const pageData = result.data?.ariesPlatformReceivableCustomerQuery?.queryPage;
  return {
    content: (pageData?.content ?? []).map(mapReceivableCustomerItem),
    totalElements: Number(pageData?.totalElements ?? 0),
  };
}

/** 统计接口原始字段（金额合计） */
export interface ReceivableCustomerStatisticalRaw {
  remainingReceivableAmt?: number | string;
  xyqxzCny?: number | string;
  yqTotalCny?: number | string;
  yqMonth01Cny?: number | string;
  yqMonth02Cny?: number | string;
  yqMonth03Cny?: number | string;
  yqMonth04Cny?: number | string;
  yqMonth05Cny?: number | string;
  yqMonth06Cny?: number | string;
  yqMonth07Cny?: number | string;
  yqMonth08Cny?: number | string;
  yqMonth09Cny?: number | string;
  yqMonth10Cny?: number | string;
  yqMonth11Cny?: number | string;
  yqMonth12Cny?: number | string;
  yqMonth13Cny?: number | string;
}

interface ReceivableCustomerStatisticalResponse {
  data?: {
    ariesPlatformReceivableCustomerQuery?: {
      queryStatisticalData?: ReceivableCustomerStatisticalRaw | null;
    };
  };
  errors?: Array<{ message?: string }>;
}

/** 列表筛选条件（不含分页） */
export type ReceivableCustomerFilterParams = Omit<
  ReceivableCustomerQueryParams,
  'currentPage' | 'size'
>;

function buildStatisticalDataGraphql(params: ReceivableCustomerFilterParams) {
  const data = buildReceivableQueryData({
    ...params,
    currentPage: 1,
    size: 1,
  });
  const lines = Object.entries(data).map(([key, value]) => {
    if (typeof value === 'boolean') {
      return `${key}: ${value}`;
    }
    return `${key}: "${escapeGraphqlString(String(value))}"`;
  });

  return `{
  ariesPlatformReceivableCustomerQuery {
    queryStatisticalData(
      data: {
        ${lines.join('\n        ')}
      }
    ) {
      remainingReceivableAmt
      xyqxzCny
      yqTotalCny
      yqMonth01Cny
      yqMonth02Cny
      yqMonth03Cny
      yqMonth04Cny
      yqMonth05Cny
      yqMonth06Cny
      yqMonth07Cny
      yqMonth08Cny
      yqMonth09Cny
      yqMonth10Cny
      yqMonth11Cny
      yqMonth12Cny
      yqMonth13Cny
    }
  }
}`;
}

function mapStatisticalToSummaryRow(
  raw: ReceivableCustomerStatisticalRaw,
): ReceivableCustomerItem {
  return {
    id: '__receivable_customer_summary__',
    isSummaryRow: true,
    receivableBalance: raw.remainingReceivableAmt,
    creditWithin: raw.xyqxzCny,
    overdueTotal: raw.yqTotalCny,
    overdue1: raw.yqMonth01Cny,
    overdue2: raw.yqMonth02Cny,
    overdue3: raw.yqMonth03Cny,
    overdue4: raw.yqMonth04Cny,
    overdue5: raw.yqMonth05Cny,
    overdue6: raw.yqMonth06Cny,
    overdue7: raw.yqMonth07Cny,
    overdue8: raw.yqMonth08Cny,
    overdue9: raw.yqMonth09Cny,
    overdue10: raw.yqMonth10Cny,
    overdue11: raw.yqMonth11Cny,
    overdue12: raw.yqMonth12Cny,
    overdueOverYear: raw.yqMonth13Cny,
  };
}

/**
 * 应收客户统计合计（与列表相同筛选条件）
 */
export async function queryReceivableCustomerStatisticalData(
  params: ReceivableCustomerFilterParams,
): Promise<ReceivableCustomerItem | null> {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildStatisticalDataGraphql(params),
    }),
  });

  if (!response.ok) {
    throw new Error(`应收客户统计接口请求失败: ${response.status}`);
  }

  const result: ReceivableCustomerStatisticalResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '应收客户统计 GraphQL 请求失败');
  }

  const raw = result.data?.ariesPlatformReceivableCustomerQuery?.queryStatisticalData;
  if (!raw) return null;
  return mapStatisticalToSummaryRow(raw);
}

/** 提交反馈入参 */
export interface SaveReceivableFeedbackParams {
  dissaleChannelCode: string;
  sapCustomerCode: string;
  /** 公司编码 */
  corpCode: string;
  feedbackMessage: string;
  expectRepayAmount: string;
  expectRepayDate: string;
  /** 附件 OBS 地址列表 */
  customerFileUrl: string[];
}

interface SaveReceivableFeedbackResponse {
  data?: {
    ariesPlatformReceivableCustomerMutation?: {
      saveAriesPlatformRofRecord?: {
        id?: string;
        dissaleChannelCode?: string;
        sapCustomerCode?: string;
        feedbackMessage?: string;
        expectRepayAmount?: string;
        expectRepayDate?: string;
        isSendEmail?: boolean;
        mailReceiver?: string | null;
        customerFileUrl?: string[];
        manualModified?: boolean;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

function escapeGraphqlString(value: string) {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
}

function buildSaveFeedbackMutation(params: SaveReceivableFeedbackParams) {
  const fileUrls = (params.customerFileUrl ?? [])
    .map((url) => `"${escapeGraphqlString(url)}"`)
    .join(', ');

  return `mutation {
  ariesPlatformReceivableCustomerMutation {
    saveAriesPlatformRofRecord(
      data: {
        dissaleChannelCode: "${escapeGraphqlString(params.dissaleChannelCode)}"
        sapCustomerCode: "${escapeGraphqlString(params.sapCustomerCode)}"
        corpCode: "${escapeGraphqlString(params.corpCode)}"
        feedbackMessage: "${escapeGraphqlString(params.feedbackMessage)}"
        expectRepayAmount: "${escapeGraphqlString(params.expectRepayAmount)}"
        expectRepayDate: "${escapeGraphqlString(params.expectRepayDate)}"
        isSendEmail: false
        mailReceiver: null
        customerFileUrl: [${fileUrls}]
      }
    ) {
      id
      dissaleChannelCode
      sapCustomerCode
      corpCode
      feedbackMessage
      expectRepayAmount
      expectRepayDate
      isSendEmail
      mailReceiver
      customerFileUrl
      manualModified
    }
  }
}`;
}

/**
 * 提交应收客户回款反馈
 */
export async function saveReceivableFeedback(params: SaveReceivableFeedbackParams) {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildSaveFeedbackMutation(params),
    }),
  });

  if (!response.ok) {
    throw new Error(`提交反馈请求失败: ${response.status}`);
  }

  const result: SaveReceivableFeedbackResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '提交反馈失败');
  }

  const saved = result.data?.ariesPlatformReceivableCustomerMutation?.saveAriesPlatformRofRecord;
  if (!saved) {
    throw new Error('提交反馈失败：无返回数据');
  }
  return saved;
}

/** 企微催办入参 */
export interface SendWechatMessageParams {
  dissaleChannelCode: string;
  sapCustomerCode: string;
  /** 公司编码 */
  corpCode: string;
  lDate: string;
}

interface SendWechatMessageResponse {
  data?: {
    ariesPlatformReceivableCustomerMutation?: {
      sendWechatMessage?: {
        id?: string;
        dissaleChannelCode?: string;
        sapCustomerCode?: string;
        custName?: string;
        salesmanUserCode?: string;
        salesmanUserName?: string;
        remainingReceivableAmt?: string | number;
        yqTotalCny?: string | number;
        feedbackStatus?: string;
        lDate?: string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

function buildSendWechatMessageMutation(params: SendWechatMessageParams) {
  return `mutation {
  ariesPlatformReceivableCustomerMutation {
    sendWechatMessage(
      data: {
        dissaleChannelCode: "${escapeGraphqlString(params.dissaleChannelCode)}"
        sapCustomerCode: "${escapeGraphqlString(params.sapCustomerCode)}"
        corpCode: "${escapeGraphqlString(params.corpCode)}"
        lDate: "${escapeGraphqlString(params.lDate)}"
      }
    ) {
      id
      dissaleChannelCode
      sapCustomerCode
      corpCode
      custName
      salesmanUserCode
      salesmanUserName
      remainingReceivableAmt
      yqTotalCny
      feedbackStatus
      lDate
    }
  }
}`;
}

/**
 * 企微催办（发送企微消息）
 */
export async function sendWechatMessage(params: SendWechatMessageParams) {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildSendWechatMessageMutation(params),
    }),
  });

  if (!response.ok) {
    throw new Error(`企微催办请求失败: ${response.status}`);
  }

  const result: SendWechatMessageResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '企微催办失败');
  }

  const data = result.data?.ariesPlatformReceivableCustomerMutation?.sendWechatMessage;
  if (!data) {
    throw new Error('企微催办失败：无返回数据');
  }
  return data;
}

interface SendWechatMessageListResponse {
  data?: {
    ariesPlatformReceivableCustomerMutation?: {
      sendWechatMessageList?: Array<{
        id?: string;
        dissaleChannelCode?: string;
        sapCustomerCode?: string;
        custName?: string;
      }>;
    };
  };
  errors?: Array<{ message?: string }>;
}

function buildSendWechatMessageListMutation(dataList: SendWechatMessageParams[]) {
  const items = dataList
    .map(
      (item) => `{
          dissaleChannelCode: "${escapeGraphqlString(item.dissaleChannelCode)}"
          sapCustomerCode: "${escapeGraphqlString(item.sapCustomerCode)}"
          corpCode: "${escapeGraphqlString(item.corpCode)}"
          lDate: "${escapeGraphqlString(item.lDate)}"
        }`,
    )
    .join('\n        ');

  return `mutation {
  ariesPlatformReceivableCustomerMutation {
    sendWechatMessageList(
      dataList: [
        ${items}
      ]
    ) {
      id
      dissaleChannelCode
      sapCustomerCode
      custName
    }
  }
}`;
}

/**
 * 批量企微催办
 */
export async function sendWechatMessageList(dataList: SendWechatMessageParams[]) {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildSendWechatMessageListMutation(dataList),
    }),
  });

  if (!response.ok) {
    throw new Error(`批量催办请求失败: ${response.status}`);
  }

  const result: SendWechatMessageListResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '批量催办失败');
  }

  const list = result.data?.ariesPlatformReceivableCustomerMutation?.sendWechatMessageList;
  if (!list) {
    throw new Error('批量催办失败：无返回数据');
  }
  return list;
}

/** 最近一次邮件收件人 */
export interface LastMailReceiver {
  id?: string;
  receiver?: string;
  createDate?: string;
}

interface QueryLastMailReceiverResponse {
  data?: {
    ariesPlatformReceivableManageQuery?: {
      queryLastMailReceiver?: LastMailReceiver | null;
    };
  };
  errors?: Array<{ message?: string }>;
}

/**
 * 查询最近一次发送邮件的收件人（发送邮件弹窗回填）
 */
export async function queryLastMailReceiver(): Promise<LastMailReceiver | null> {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: `{
  ariesPlatformReceivableManageQuery {
    queryLastMailReceiver(data: {}) {
      receiver
    }
  }
}`,
    }),
  });

  if (!response.ok) {
    throw new Error(`查询最近收件人失败: ${response.status}`);
  }

  const result: QueryLastMailReceiverResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询最近收件人失败');
  }

  return result.data?.ariesPlatformReceivableManageQuery?.queryLastMailReceiver ?? null;
}

export interface SendReceivableEmailParams {
  /** 1=按汇总，2=按分公司，3=按分公司指定人（数据合并成一封） */
  emailType: '1' | '2' | '3';
  /** 按汇总、按分公司指定人必传 */
  receiver?: string;
  /** 按分公司、按分公司指定人必传：选中的分公司编码 */
  saleOfficeCodeList?: string[];
}

interface SendReceivableEmailResponse {
  data?: {
    ariesPlatformReceivableManageMutation?: {
      sendEmail?: {
        emailType?: string;
        receiver?: string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

function buildSendEmailMutation(params: SendReceivableEmailParams) {
  const officeCodes = [...new Set((params.saleOfficeCodeList ?? []).map((code) => code.trim()).filter(Boolean))]
    .map((code) => `"${escapeGraphqlString(code)}"`)
    .join(', ');
  let fields = `emailType: "${params.emailType}"`;
  if (params.emailType === '1' || params.emailType === '3') {
    fields += `
        receiver: "${escapeGraphqlString(params.receiver ?? '')}"`;
  }
  if (params.emailType === '2' || params.emailType === '3') {
    fields += `
        saleOfficeCodeList: [${officeCodes}]`;
  }

  return `mutation {
  ariesPlatformReceivableManageMutation {
    sendEmail(
      data: {
        ${fields}
      }
    ) {
      emailType
      receiver
    }
  }
}`;
}

/**
 * 应收报表发送邮件
 */
export async function sendReceivableEmail(params: SendReceivableEmailParams) {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildSendEmailMutation(params),
    }),
  });

  if (!response.ok) {
    throw new Error(`发送邮件失败: ${response.status}`);
  }

  const result: SendReceivableEmailResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '发送邮件失败');
  }

  const saved = result.data?.ariesPlatformReceivableManageMutation?.sendEmail;
  if (!saved) {
    throw new Error('发送邮件失败：无返回数据');
  }
  return saved;
}

export interface FollowUpQueryParams {
  dissaleChannelCode: string;
  sapCustomerCode: string;
  /** 公司编码 */
  corpCode: string;
}

/** 跟进反馈记录 */
export interface FollowUpFeedbackRecordLogItem {
  feedbackMessage?: string;
  expectRepayAmount?: string | number;
  expectRepayDate?: string;
  /** 附件 URL，可能是单个字符串或数组 */
  customerFileUrl?: string | string[];
  createDate?: string;
  createName?: string;
  createWorkCode?: string;
}

/** 跟进风险修改记录 */
export interface FollowUpRiskRecordLogItem {
  originRiskLevel?: string;
  riskLevel?: string;
  riskReason?: string;
  riskDesc?: string;
  isProsecute?: number | string | boolean;
  manualModified?: number | string | boolean;
  createName?: string;
  createWorkCode?: string;
  createDate?: string;
}

export interface FollowUpRecordsResult {
  feedbackLogs: FollowUpFeedbackRecordLogItem[];
  riskLogs: FollowUpRiskRecordLogItem[];
}

interface QueryFollowUpRecordsResponse {
  data?: {
    ariesPlatformReceivableCustomerQuery?: {
      queryFollowUpFeedbackRecordLog?: FollowUpFeedbackRecordLogItem[];
      queryFollowUpRiskRecordLog?: FollowUpRiskRecordLogItem[];
    };
  };
  errors?: Array<{ message?: string }>;
}

function buildFollowUpRecordsGraphql(params: FollowUpQueryParams) {
  const channel = escapeGraphqlString(params.dissaleChannelCode);
  const customer = escapeGraphqlString(params.sapCustomerCode);
  const corp = escapeGraphqlString(params.corpCode);
  return `{
  ariesPlatformReceivableCustomerQuery {
    queryFollowUpFeedbackRecordLog(
      data: {
        dissaleChannelCode: "${channel}"
        sapCustomerCode: "${customer}"
        corpCode: "${corp}"
      }
    ) {
      feedbackMessage
      expectRepayAmount
      expectRepayDate
      customerFileUrl
      createDate
      createWorkCode
    }
    queryFollowUpRiskRecordLog(
      data: {
        dissaleChannelCode: "${channel}"
        sapCustomerCode: "${customer}"
        corpCode: "${corp}"
      }
    ) {
      originRiskLevel
      riskLevel
      riskReason
      riskDesc
      isProsecute
      manualModified
      createName
      createWorkCode
      createDate
    }
  }
}`;
}

/**
 * 查询应收客户跟进记录（反馈记录 + 风险修改记录）
 */
export async function queryFollowUpRecords(
  params: FollowUpQueryParams,
): Promise<FollowUpRecordsResult> {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildFollowUpRecordsGraphql(params),
    }),
  });

  if (!response.ok) {
    throw new Error(`查询跟进记录失败: ${response.status}`);
  }

  const result: QueryFollowUpRecordsResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询跟进记录失败');
  }

  const data = result.data?.ariesPlatformReceivableCustomerQuery;
  return {
    feedbackLogs: data?.queryFollowUpFeedbackRecordLog ?? [],
    riskLogs: data?.queryFollowUpRiskRecordLog ?? [],
  };
}

